import { Component, ViewEncapsulation, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { NegotiationsService } from 'src/app/services/negotiations/negotiations.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { UserService } from 'src/app/services/user/user.service';
import { Negotiation, PaginationInfo, PaginatedResponse, NegotiationFilters, FilterDropdownData, FilterOption } from 'src/app/shared/models';
import { SearchableSelectOption } from 'src/app/shared/components/searchable-select/searchable-select.component';
import { NegotiationEditModalService } from 'src/app/services/negotiation-edit-modal/negotiation-edit-modal.service';
import { NegotiationChatModalService } from 'src/app/services/negotiation-chat-modal/negotiation-chat-modal.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
declare var KTMenu: any;
declare var bootstrap: any;

@Component({
  selector: 'app-negotiations',
  templateUrl: './negotiations.component.html',
  styleUrls: ['./negotiations.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class NegotiationsComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  negotiations: Negotiation[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFilters: boolean = false;

  // Filter properties
  filters: NegotiationFilters = {};
  filterDropdownData: FilterDropdownData = {
    makes: [],
    models: [],
    statuses: [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'in_process', label: 'In Process' }
    ],
    transactionStages: [
      { value: 'inquiry', label: 'Inquiry' },
      { value: 'negotiation', label: 'Negotiation' },
      { value: 'agreement', label: 'Agreement' },
      { value: 'payment', label: 'Payment' },
      { value: 'delivery', label: 'Delivery' }
    ],
    countries: [],
    sources: [
      { value: 'website', label: 'Website' },
      { value: 'phone', label: 'Phone' },
      { value: 'email', label: 'Email' },
      { value: 'referral', label: 'Referral' }
    ]
  };

  // Searchable select options (converted from FilterDropdownData)
  makeOptions: SearchableSelectOption[] = [];
  modelOptions: SearchableSelectOption[] = [];
  statusOptions: SearchableSelectOption[] = [];
  transactionStageOptions: SearchableSelectOption[] = [];
  countryOptions: SearchableSelectOption[] = [];
  sourceOptions: SearchableSelectOption[] = [];
  yearOptions: SearchableSelectOption[] = [];

  // Sales person list
  salesPersonList: SearchableSelectOption[] = [];

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  constructor(
    private negotiationsService: NegotiationsService,
    private generalService: GeneralService,
    private userService: UserService,
    private negotiationEditModalService: NegotiationEditModalService,
    private negotiationChatModalService: NegotiationChatModalService
  ) {
    super();
    this.setupSearchDebounce();
  }

  ngOnInit(): void {
    this.loadFilterData();
    this.loadSalesPersonList();
    this.loadNegotiations();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  private setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.loadNegotiations(1, searchTerm, this.filters);
      })
    );
  }

  loadNegotiations(page: number = this.currentPage, search: string = this.searchTerm, filters: NegotiationFilters = this.filters): void {
    this.isLoading = true;
    this.currentPage = page;

    this.subscriptions.push(
      this.negotiationsService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<Negotiation>) => {
          if (response.status && response.data) {
            this.negotiations = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            this.refreshMenus();
          } else {
            this.negotiations = [];
            this.resetPagination();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading negotiations:', error);
          this.negotiations = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadNegotiations(page);
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next('');
  }

  // Filter methods
  loadFilterData(): void {
    // Load makes from API
    this.subscriptions.push(
      this.generalService.GetMakes().subscribe({
        next: (response) => {
          if (response.status && response.data) {
            this.filterDropdownData.makes = response.data.map((make: any) => ({
              value: make._id,
              label: make.name
            }));
            this.makeOptions = this.filterDropdownData.makes.map(item => ({ value: item.value, label: item.label }));
          }
        },
        error: (error) => {
          console.error('Error loading makes:', error);
          // Use fallback data if API fails
          this.filterDropdownData.makes = [
            { value: 'toyota', label: 'Toyota' },
            { value: 'honda', label: 'Honda' },
            { value: 'nissan', label: 'Nissan' }
          ];
          this.makeOptions = this.filterDropdownData.makes.map(item => ({ value: item.value, label: item.label }));
        }
      })
    );

    // Load models from API
    this.subscriptions.push(
      this.generalService.GetModels().subscribe({
        next: (response) => {
          if (response.status && response.data) {
            this.filterDropdownData.models = response.data.map((model: any) => ({
              value: model._id,
              label: model.name
            }));
            this.modelOptions = this.filterDropdownData.models.map(item => ({ value: item.value, label: item.label }));
          }
        },
        error: (error) => {
          console.error('Error loading models:', error);
          // Use fallback data if API fails
          this.filterDropdownData.models = [
            { value: 'camry', label: 'Camry' },
            { value: 'corolla', label: 'Corolla' },
            { value: 'civic', label: 'Civic' }
          ];
          this.modelOptions = this.filterDropdownData.models.map(item => ({ value: item.value, label: item.label }));
        }
      })
    );

    // Set up other static filter data
    this.filterDropdownData.countries = [
      { value: 'japan', label: 'Japan' },
      { value: 'usa', label: 'USA' },
      { value: 'germany', label: 'Germany' },
      { value: 'uk', label: 'United Kingdom' },
      { value: 'canada', label: 'Canada' }
    ];

    // Convert FilterDropdownData to SearchableSelectOption format for static data
    this.statusOptions = this.filterDropdownData.statuses.map(item => ({ value: item.value, label: item.label }));
    this.transactionStageOptions = this.filterDropdownData.transactionStages.map(item => ({ value: item.value, label: item.label }));
    this.countryOptions = this.filterDropdownData.countries.map(item => ({ value: item.value, label: item.label }));
    this.sourceOptions = this.filterDropdownData.sources.map(item => ({ value: item.value, label: item.label }));

    // Generate year options
    this.yearOptions = this.getYearOptions().map(year => ({ value: year, label: year.toString() }));
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    this.loadNegotiations(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {};
    this.loadNegotiations(1, this.searchTerm, this.filters);
  }

  onFilterChange(): void {
    // Filters will only be applied when search button is clicked
    // This method can be used for any filter-related UI updates if needed
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filters).some(value =>
      value !== undefined && value !== null && value !== ''
    );
  }

  getYearOptions(): number[] {
    const currentYear = new Date().getFullYear();
    const startYear = 1990;
    const years: number[] = [];

    for (let year = currentYear; year >= startYear; year--) {
      years.push(year);
    }

    return years;
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  private initializeMenus(): void {
    // Wait for DOM to be ready, then initialize menus
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Call this after negotiations data is loaded
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  trackByNegotiationId(index: number, negotiation: Negotiation): string {
    return negotiation._id || index.toString();
  }

  getCarImage(negotiation: Negotiation): string {
    if (negotiation.car?.pictures && negotiation.car.pictures.length > 0) {
      return negotiation.car.pictures[0];
    }
    return '/assets/media/stock/600x400/img-1.jpg'; // Default placeholder image
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'badge-light-warning';
      case 'approved':
        return 'badge-light-success';
      case 'rejected':
        return 'badge-light-danger';
      case 'in_process':
        return 'badge-light-primary';
      default:
        return 'badge-light-secondary';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Load sales person list
  loadSalesPersonList(): void {
    this.subscriptions.push(
      this.userService.GetData('1', '', { role_type: 30, paginate: 'false' }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const salesPersons = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.salesPersonList = salesPersons.map((person: any) => ({
              value: person._id || person.doc_id || '',
              label: `${person.title || ''} ${person.name || ''}`.trim()
            }));
          }
        },
        error: (error) => {
          console.error('Error loading sales person list:', error);
          this.salesPersonList = [];
        }
      })
    );
  }

  // Get sales person name
  getSalesPersonName(negotiation: Negotiation): string {
    if (negotiation.sales_person) {
      return `${negotiation.sales_person.title || ''} ${negotiation.sales_person.name || ''}`.trim();
    }
    return '-';
  }

  // Open edit modal
  openEditModal(negotiation: Negotiation): void {
    this.negotiationEditModalService.setSelectedNegotiation(negotiation);
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_negotiation_edit');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Open chat modal
  openChatModal(negotiation: Negotiation): void {
    this.negotiationChatModalService.setSelectedNegotiation(negotiation);
    setTimeout(() => {
      const modalElement = document.getElementById('kt_modal_negotiation_chat');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Refresh negotiations list (called after edit)
  onNegotiationUpdated(): void {
    this.loadNegotiations(this.currentPage);
  }

}
